/**
 * Tab "Integraciones" del CenterDrawer — gestiona el conector de Google
 * Calendar **del centro** (owner_type='center'). Cuando una booking del centro
 * no tiene staff con su Google personal, este token es el fallback.
 *
 * Acceso: solo superadmin puede mutar (igual que el resto de edición del
 * centro, post #145). Admin del centro ve el estado pero no puede activar
 * o desactivar.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { adminGoogleApi, type GoogleStatus } from '@api/client';

interface Props {
  centerId: string;
  canMutate: boolean;
}

export function CenterDrawerIntegracionesTab({ centerId, canMutate }: Props) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['google', 'status', 'center', centerId];
  const { data, isLoading } = useQuery<GoogleStatus>({
    queryKey,
    queryFn: () => adminGoogleApi.status('center', centerId),
  });

  const connectMutation = useMutation({
    mutationFn: async () => {
      await adminGoogleApi.connect('center', centerId);
      await adminGoogleApi.completeMock('center', centerId);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey }); setError(null); },
    onError: () => { setError('No se pudo conectar Google Calendar.'); },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      await adminGoogleApi.disconnect('center', centerId);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey }); setError(null); },
    onError: () => { setError('No se pudo desconectar Google Calendar.'); },
  });

  const connected = data?.connected === true;
  const busy = connectMutation.isPending || disconnectMutation.isPending;

  return (
    <div>
      <div style={{
        padding: 14,
        marginBottom: 14,
        background: 'var(--bg-soft)',
        border: '1px dashed var(--border-strong)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 12.5,
        color: 'var(--fg-muted)',
      }}>
        <strong>Google Calendar del centro.</strong> Si un staff de este centro no tiene
        su Google personal conectado, las reservas de ese staff se sincronizan con
        este calendar del centro. <span className="mono" style={{ fontSize: 11 }}>MOCK</span>:
        esta versión no llama a Google realmente.
      </div>
      <div className="field" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13 }}>
          {isLoading && <span style={{ color: 'var(--fg-soft)' }}>Comprobando estado…</span>}
          {!isLoading && connected && (
            <span>
              <span className="pill" data-tone="ok" style={{ marginRight: 8 }}>Conectado</span>
              {data.connectedAt && (
                <span className="mono" style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  desde {new Date(data.connectedAt).toLocaleDateString('es-ES')}
                </span>
              )}
            </span>
          )}
          {!isLoading && !connected && (
            <span className="pill" data-tone="neutral">No conectado</span>
          )}
        </div>
        <div>
          {connected ? (
            <button
              className="btn"
              data-variant="danger"
              disabled={busy || !canMutate}
              onClick={() => { disconnectMutation.mutate(); }}
              title={!canMutate ? 'Solo superadmin puede desconectar' : undefined}
            >
              {disconnectMutation.isPending ? 'Desconectando…' : 'Desconectar'}
            </button>
          ) : (
            <button
              className="btn"
              data-variant="primary"
              disabled={busy || !canMutate}
              onClick={() => { connectMutation.mutate(); }}
              title={!canMutate ? 'Solo superadmin puede conectar' : undefined}
            >
              {connectMutation.isPending ? 'Conectando…' : 'Conectar (mock)'}
            </button>
          )}
        </div>
      </div>
      {error && (
        <div style={{ marginTop: 10, padding: 8, background: 'var(--danger-soft)', borderRadius: 4, fontSize: 12, color: 'var(--danger)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
